// client/src/components/AvailabilityModal.js
import { useState, useEffect, useMemo } from 'react'
import { format } from 'date-fns'
import { fromZonedTime } from 'date-fns-tz'
import {
  generateRRule,
  describeRecurrence,
  countOccurrences,
} from '../../utils/recurrence.js'
import { AVAILABILITY_INFO } from '../../constants/availabilityMessages.js'
import {
  formatAvailabilityTime,
  parseAvailabilityTime,
  formatAvailabilityTimeFromDate,
  formatAvailabilityDisplayTime,
  getNextHalfHourAvailabilityTime,
} from '../../utils/availabilityTime.js'
import DayPicker from './DayPicker.js'
import './AvailabilityModal.css'

const TZ = Intl.DateTimeFormat().resolvedOptions().timeZone

const DAY_CODE_TO_INDEX = {
  SU: 0,
  MO: 1,
  TU: 2,
  WE: 3,
  TH: 4,
  FR: 5,
  SA: 6,
}

const DAY_INDEX_TO_CODE = Object.fromEntries(
  Object.entries(DAY_CODE_TO_INDEX).map(([code, index]) => [index, code]),
)

// Helper function to format a Date object as YYYY-MM-DD for input[type="date"]
const formatDateForInput = (dateObj) => {
  const year = dateObj.getFullYear()
  const month = String(dateObj.getMonth() + 1).padStart(2, '0')
  const day = String(dateObj.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const getRecurringAnchorDate = (baseDate, selectedDays) => {
  if (!(baseDate instanceof Date) || isNaN(baseDate.getTime())) {
    return baseDate
  }

  const selectedDayIndexes = [...new Set(selectedDays)]
    .map((dayCode) => DAY_CODE_TO_INDEX[dayCode])
    .filter((dayIndex) => Number.isInteger(dayIndex))
    .sort((left, right) => left - right)

  if (selectedDayIndexes.length === 0) {
    return baseDate
  }

  const baseDay = baseDate.getDay()
  const offset = Math.min(
    ...selectedDayIndexes.map((dayIndex) => {
      const rawOffset = dayIndex - baseDay
      return rawOffset >= 0 ? rawOffset : rawOffset + 7
    }),
  )

  const anchorDate = new Date(baseDate)
  anchorDate.setDate(anchorDate.getDate() + offset)
  return anchorDate
}

function AvailabilityModal({
  isOpen,
  onClose,
  onSave,
  onDelete,
  mode = 'create', // 'create' or 'edit'
  existingAvailability = null,
  selectedDate = null,
  selectedHour = null,
  leagueId = null,
  divisionId = null,
  teamId = null,
  leagueEndDate = null,
  isDoublesLeague = false,
}) {
  const [date, setDate] = useState('')
  const [startTime, setStartTime] = useState('')
  const [endTime, setEndTime] = useState('')
  const [loading, setLoading] = useState(false)
  // Progressive-disclosure UI state
  const [infoExpanded, setInfoExpanded] = useState(false)
  const [isDateEditing, setIsDateEditing] = useState(false)
  const [applyTo, setApplyTo] = useState('team')

  // Recurrence state
  const [isRecurring, setIsRecurring] = useState(false)
  const [selectedDays, setSelectedDays] = useState([])
  const [interval, setInterval] = useState(1)
  const [endType, setEndType] = useState('league')
  const [customEndDate, setCustomEndDate] = useState('')
  const isSharedTeamAvailability = Boolean(
    existingAvailability?.shared_team_slot_id,
  )

  const confirmSharedToIndividualChange = () => {
    if (
      mode !== 'edit' ||
      !isSharedTeamAvailability ||
      applyTo !== 'individual'
    ) {
      return true
    }

    return window.confirm(
      'Switching this shared slot to "Just me" removes it for both you and your partner and recreates it only for you. Your partner will need to add their own availability again. Continue?',
    )
  }

  const getDeleteConfirmationMessage = (deleteType) => {
    if (!isSharedTeamAvailability) {
      if (deleteType === 'series') {
        return 'Delete this recurring availability? This will remove all future occurrences in the series.'
      }

      if (deleteType === 'instance') {
        return 'Delete only this occurrence? The recurring pattern will remain, but this specific date will be skipped.'
      }

      return 'This removes your personal slot.'
    }

    if (deleteType === 'series') {
      return 'This removes the recurring slot for both you and your partner.'
    }

    if (deleteType === 'instance') {
      return 'This removes this occurrence for both you and your partner.'
    }

    return 'This removes the slot for both you and your partner.'
  }

  // Generate all time options with 30-minute increments (6am to 10pm)
  const allTimeOptions = useMemo(() => {
    const options = []
    for (let hour = 6; hour <= 22; hour++) {
      const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour
      const ampm = hour >= 12 ? 'PM' : 'AM'

      // Add :00 option
      options.push({
        value: formatAvailabilityTime(hour, 0),
        label: `${displayHour}:00 ${ampm}`,
      })

      // Add :30 option (except for the last hour)
      if (hour < 22) {
        options.push({
          value: formatAvailabilityTime(hour, 30),
          label: `${displayHour}:30 ${ampm}`,
        })
      }
    }
    return options
  }, [])

  // Filter end time options based on selected start time
  const endTimeOptions = useMemo(() => {
    if (!startTime) {
      return allTimeOptions
    }

    const parsedStartTime = parseAvailabilityTime(startTime)
    if (!parsedStartTime) {
      return allTimeOptions
    }

    // Filter to only show times after the start time
    return allTimeOptions.filter((option) => {
      const parsedOption = parseAvailabilityTime(option.value)
      return (
        parsedOption && parsedOption.totalMinutes > parsedStartTime.totalMinutes
      )
    })
  }, [startTime, allTimeOptions])

  // Clear end time if it's before or equal to the new start time
  useEffect(() => {
    if (startTime && endTime) {
      const parsedStartTime = parseAvailabilityTime(startTime)
      const parsedEndTime = parseAvailabilityTime(endTime)

      if (
        parsedStartTime &&
        parsedEndTime &&
        parsedEndTime.totalMinutes <= parsedStartTime.totalMinutes
      ) {
        setEndTime('')
      }
    }
  }, [startTime, endTime])

  useEffect(() => {
    if (isDoublesLeague) {
      setApplyTo(
        mode === 'edit' && !existingAvailability?.shared_team_slot_id
          ? 'individual'
          : 'team',
      )
    } else {
      setApplyTo('individual')
    }

    if (mode === 'edit' && existingAvailability) {
      // Use local hours, not UTC, for pre-filling
      const startDate = new Date(existingAvailability.start_time)
      const endDate = new Date(existingAvailability.end_time)

      // Set the date for edit mode using local date
      setDate(formatDateForInput(startDate))

      setStartTime(formatAvailabilityTimeFromDate(startDate))
      setEndTime(formatAvailabilityTimeFromDate(endDate))

      // If editing a recurring availability, populate recurrence fields
      if (existingAvailability.is_recurring) {
        setIsRecurring(true)
        // Note: We can't edit recurrence rules in edit mode for now
        // This would require parsing the RRULE string
      }
    } else if (mode === 'create' && selectedHour !== null) {
      // Pre-fill with selected date using local date parts
      if (selectedDate) {
        setDate(formatDateForInput(selectedDate))
      }

      // Pre-fill with selected time slot (now supports minutes)
      if (typeof selectedHour === 'object') {
        setStartTime(
          formatAvailabilityTime(selectedHour.hour, selectedHour.minute),
        )
        setEndTime(getNextHalfHourAvailabilityTime(selectedHour))
      } else {
        // Legacy support for hour-only
        setStartTime(formatAvailabilityTime(selectedHour, 0))
        setEndTime(formatAvailabilityTime(selectedHour + 1, 0))
      }

      // Pre-select the day of week based on selectedDate for recurring availability
      if (selectedDate) {
        const dayOfWeek = selectedDate.getDay() // 0 = Sunday, 1 = Monday, etc.
        const dayCode = DAY_INDEX_TO_CODE[dayOfWeek]
        if (dayCode) {
          setSelectedDays([dayCode])
        }
      }
    } else {
      // Reset form - set date to selected date or empty
      if (selectedDate && mode === 'create') {
        setDate(formatDateForInput(selectedDate))
      } else {
        setDate('')
      }
      setStartTime('')
      setEndTime('')
      setIsRecurring(false)
      setSelectedDays([])
      setInterval(1)
      setEndType('league')
      setCustomEndDate('')
      setIsDateEditing(false)
      setInfoExpanded(false)
    }
  }, [
    mode,
    existingAvailability,
    selectedHour,
    selectedDate,
    isOpen,
    isDoublesLeague,
  ])

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!date || !date.match(/^\d{4}-\d{2}-\d{2}$/)) {
      alert('Please select a valid date')
      return
    }

    if (!startTime || !endTime) {
      alert('Please select both start and end times')
      return
    }

    const parsedStartTime = parseAvailabilityTime(startTime)
    const parsedEndTime = parseAvailabilityTime(endTime)

    if (
      !parsedStartTime ||
      !parsedEndTime ||
      parsedEndTime.totalMinutes <= parsedStartTime.totalMinutes
    ) {
      alert('End time must be after start time')
      return
    }

    // Validate recurring availability
    if (isRecurring && mode === 'create' && selectedDays.length === 0) {
      alert('Please select at least one day for recurring availability')
      return
    }

    if (
      isRecurring &&
      mode === 'create' &&
      endType === 'date' &&
      !customEndDate
    ) {
      alert('Please select an end date')
      return
    }

    // Validate custom end date doesn't exceed league end
    if (isRecurring && endType === 'date' && customEndDate && leagueEndDate) {
      const customEnd = new Date(`${customEndDate}T23:59:59Z`)
      if (customEnd > new Date(leagueEndDate)) {
        alert('End date cannot exceed league end date')
        return
      }
    }

    setLoading(true)

    try {
      // Use the date from state instead of from props
      // Parse date string to avoid timezone issues
      const [yearNum, monthNum, dayNum] = date.split('-').map(Number)
      const dateToUse = new Date(yearNum, monthNum - 1, dayNum) // month is 0-indexed

      const anchorDate =
        isRecurring && mode === 'create'
          ? getRecurringAnchorDate(dateToUse, selectedDays)
          : dateToUse

      const year = anchorDate.getFullYear()
      const month = anchorDate.getMonth()
      const day = anchorDate.getDate()

      // Create local Date objects and convert to UTC
      const startLocal = new Date(
        year,
        month,
        day,
        parsedStartTime.hour,
        parsedStartTime.minute,
      )
      const endLocal = new Date(
        year,
        month,
        day,
        parsedEndTime.hour,
        parsedEndTime.minute,
      )

      const startTimeISO = fromZonedTime(startLocal, TZ).toISOString()
      const endTimeISO = fromZonedTime(endLocal, TZ).toISOString()

      if (mode === 'edit' && existingAvailability.is_recurring) {
        // Exception-based approach: Add this date to exception_dates, create standalone
        if (
          !window.confirm(
            'Edit this occurrence? This will create a standalone time slot with your changes. The recurring pattern will continue for other dates.',
          )
        ) {
          setLoading(false)
          return
        }

        if (!confirmSharedToIndividualChange()) {
          setLoading(false)
          return
        }

        const isExpandedInstance =
          existingAvailability._expanded && existingAvailability._original_id

        // Get the original availability ID and the instance date
        const originalId = isExpandedInstance
          ? existingAvailability._original_id
          : existingAvailability.id

        // Get the date of this specific instance (YYYY-MM-DD format)
        const instanceDate = new Date(existingAvailability.start_time)
          .toISOString()
          .split('T')[0]

        // Add exception to the series (removes this date from recurring pattern)
        if (isSharedTeamAvailability) {
          await onDelete(originalId, instanceDate, {
            sharedTeamSlotId: existingAvailability.shared_team_slot_id,
          })
        } else {
          await onDelete(originalId, instanceDate)
        }

        // Create new standalone availability with edited times
        const newAvailabilityData = {
          start_time: startTimeISO,
          end_time: endTimeISO,
          is_recurring: false,
          // League and division will be added by the wrapper
        }
        if (isDoublesLeague) {
          newAvailabilityData.apply_to = applyTo
          if (applyTo === 'team') {
            newAvailabilityData.team_id = teamId
          }
        }

        await onSave(newAvailabilityData)
        onClose()
      } else {
        // Non-recurring availability or create mode
        const availabilityData = {
          start_time: startTimeISO,
          end_time: endTimeISO,
        }

        if (mode === 'edit') {
          availabilityData.id = existingAvailability.id
          if (existingAvailability.shared_team_slot_id) {
            availabilityData.shared_team_slot_id =
              existingAvailability.shared_team_slot_id
          }
        } else {
          // Add league and division context for create mode
          if (leagueId) availabilityData.league_id = leagueId
          if (divisionId) availabilityData.division_id = divisionId
        }

        if (isDoublesLeague) {
          availabilityData.apply_to = applyTo
          if (applyTo === 'team') {
            availabilityData.team_id = teamId
            if (existingAvailability?.shared_team_slot_id) {
              availabilityData.shared_team_slot_id =
                existingAvailability.shared_team_slot_id
            }
          }
        }

        // Add recurrence fields if recurring
        if (isRecurring && mode === 'create') {
          let endDate

          if (endType === 'league' && leagueEndDate) {
            endDate = new Date(leagueEndDate)
          } else if (endType === 'date' && customEndDate) {
            endDate = new Date(`${customEndDate}T23:59:59Z`)
          }

          try {
            // Use the LOCAL date/time for RRULE generation, not the UTC converted time
            // This ensures RRULE DTSTART matches the day-of-week the user selected
            const localStartDate = new Date(
              year,
              month,
              day,
              parsedStartTime.hour,
              parsedStartTime.minute,
            )

            const rruleString = generateRRule({
              days: selectedDays,
              interval,
              startDate: localStartDate,
              endDate,
            })

            availabilityData.is_recurring = true
            availabilityData.recurrence_rule = rruleString
          } catch (error) {
            alert(`Error generating recurrence rule: ${error.message}`)
            setLoading(false)
            return
          }
        }

        if (!confirmSharedToIndividualChange()) {
          setLoading(false)
          return
        }

        await onSave(availabilityData)
        onClose()
      }
    } catch (error) {
      console.error('Error saving availability:', error)
      alert('Failed to save availability. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async () => {
    if (!existingAvailability) return

    if (!window.confirm(getDeleteConfirmationMessage('single'))) {
      return
    }

    // For non-recurring or when deleting the entire series, just use the actual ID
    // Don't use _original_id here as that would delete the parent recurring series
    const availabilityId = existingAvailability.id

    setLoading(true)
    try {
      if (existingAvailability.shared_team_slot_id) {
        await onDelete(availabilityId, null, {
          sharedTeamSlotId: existingAvailability.shared_team_slot_id,
        })
      } else {
        await onDelete(availabilityId)
      }
      onClose()
    } catch (error) {
      console.error('Error deleting availability:', error)
      alert('Failed to delete availability. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteSeries = async () => {
    if (!existingAvailability) return

    if (!window.confirm(getDeleteConfirmationMessage('series'))) {
      return
    }

    // Determine if this is an expanded instance of a recurring availability
    const isExpandedInstance =
      existingAvailability._expanded && existingAvailability._original_id
    const availabilityId = isExpandedInstance
      ? existingAvailability._original_id
      : existingAvailability.id

    setLoading(true)
    try {
      if (existingAvailability.shared_team_slot_id) {
        await onDelete(availabilityId, null, {
          sharedTeamSlotId: existingAvailability.shared_team_slot_id,
        })
      } else {
        await onDelete(availabilityId)
      }
      onClose()
    } catch (error) {
      console.error('Error deleting availability series:', error)
      alert(
        `Failed to delete availability: ${error.message || 'Please try again.'}`,
      )
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteThisInstance = async () => {
    if (!existingAvailability) return

    if (!window.confirm(getDeleteConfirmationMessage('instance'))) {
      return
    }

    // Determine the availability ID and the date to exclude
    const isExpandedInstance =
      existingAvailability._expanded && existingAvailability._original_id
    const availabilityId = isExpandedInstance
      ? existingAvailability._original_id
      : existingAvailability.id

    // Get the date of this specific instance
    const instanceDate = new Date(existingAvailability.start_time)
      .toISOString()
      .split('T')[0]

    setLoading(true)
    try {
      // Call onDelete with the exception date parameter
      if (existingAvailability.shared_team_slot_id) {
        await onDelete(availabilityId, instanceDate, {
          sharedTeamSlotId: existingAvailability.shared_team_slot_id,
        })
      } else {
        await onDelete(availabilityId, instanceDate)
      }
      onClose()
    } catch (error) {
      console.error('Error deleting availability instance:', error)
      alert(
        `Failed to delete this occurrence: ${error.message || 'Please try again.'}`,
      )
    } finally {
      setLoading(false)
    }
  }

  const handleUpdateSeries = async () => {
    if (!existingAvailability) return

    if (
      !window.confirm(
        'Update all occurrences in this series with the new times?',
      )
    ) {
      return
    }

    if (!startTime || !endTime) {
      alert('Please select both start and end times')
      return
    }

    const parsedStartTime = parseAvailabilityTime(startTime)
    const parsedEndTime = parseAvailabilityTime(endTime)

    if (
      !parsedStartTime ||
      !parsedEndTime ||
      parsedEndTime.totalMinutes <= parsedStartTime.totalMinutes
    ) {
      alert('End time must be after start time')
      return
    }

    if (!confirmSharedToIndividualChange()) {
      return
    }

    setLoading(true)

    try {
      // Use the date from state
      // Parse date string to avoid timezone issues
      const [yearNum, monthNum, dayNum] = date.split('-').map(Number)
      const dateToUse = new Date(yearNum, monthNum - 1, dayNum) // month is 0-indexed
      const year = dateToUse.getFullYear()
      const month = dateToUse.getMonth()
      const day = dateToUse.getDate()

      // Create local Date objects with new times and convert to UTC
      const startLocal = new Date(
        year,
        month,
        day,
        parsedStartTime.hour,
        parsedStartTime.minute,
      )
      const endLocal = new Date(
        year,
        month,
        day,
        parsedEndTime.hour,
        parsedEndTime.minute,
      )

      const startTimeISO = fromZonedTime(startLocal, TZ).toISOString()
      const endTimeISO = fromZonedTime(endLocal, TZ).toISOString()

      const isExpandedInstance =
        existingAvailability._expanded && existingAvailability._original_id
      const availabilityId = isExpandedInstance
        ? existingAvailability._original_id
        : existingAvailability.id

      const availabilityData = {
        id: availabilityId,
        start_time: startTimeISO,
        end_time: endTimeISO,
        is_recurring: true,
        recurrence_rule: existingAvailability.recurrence_rule,
      }
      if (isDoublesLeague) {
        availabilityData.apply_to = applyTo
        if (applyTo === 'team') {
          availabilityData.team_id = teamId
          availabilityData.shared_team_slot_id =
            existingAvailability.shared_team_slot_id
        }
      }

      await onSave(availabilityData)
      onClose()
    } catch (error) {
      console.error('Error updating recurring series:', error)
      alert('Failed to update recurring series. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  // Generate preview text for recurring availability
  const getPreviewText = () => {
    if (!isRecurring || selectedDays.length === 0 || !startTime || !endTime) {
      return null
    }

    const startDate = date ? new Date(date) : new Date()
    let endDate

    if (endType === 'league' && leagueEndDate) {
      endDate = new Date(leagueEndDate)
    } else if (endType === 'date' && customEndDate) {
      endDate = new Date(`${customEndDate}T23:59:59Z`)
    }

    const description = describeRecurrence(selectedDays, interval, endDate)

    const startTimeFormatted = formatAvailabilityDisplayTime(startTime)
    const endTimeFormatted = formatAvailabilityDisplayTime(endTime)

    let preview = `${description} from ${startTimeFormatted} - ${endTimeFormatted}`

    // Add occurrence count if we have all the data
    if (endDate) {
      try {
        const count = countOccurrences({
          days: selectedDays,
          interval,
          startDate,
          endDate,
        })
        if (count > 0) {
          preview += ` (${count} time slot${count !== 1 ? 's' : ''} will be created)`
        }
      } catch (error) {
        console.error('Error counting occurrences:', error)
      }
    }

    return preview
  }

  if (!isOpen) return null

  const modalTitle = mode === 'edit' ? 'Edit Availability' : 'Add Availability'

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="availability-modal" onClick={(e) => e.stopPropagation()}>
        {/* ── Header ──────────────────────────────────────────────── */}
        <div className="modal-header">
          <h3>{modalTitle}</h3>
          <div className="modal-header-right">
            <button
              type="button"
              className="info-toggle-btn"
              onClick={() => setInfoExpanded((p) => !p)}
              aria-expanded={infoExpanded}
            >
              {infoExpanded ? 'Got it' : 'How does this work?'}
            </button>
            <button
              className="modal-close"
              onClick={onClose}
              aria-label="Close modal"
            >
              ×
            </button>
          </div>
        </div>

        {/* ── Expandable info box ──────────────────────────────────── */}
        {infoExpanded && (
          <div
            className="availability-info-box"
            role="status"
            data-testid="availability-info-box"
          >
            <div className="info-content">
              <strong>{AVAILABILITY_INFO.HEADING}</strong>
              <p>{AVAILABILITY_INFO.COMPACT_DESCRIPTION}</p>
            </div>
          </div>
        )}

        {/* ── Form: scrollable body + sticky footer ────────────────── */}
        <form
          id="availability-form"
          className="modal-form"
          onSubmit={handleSubmit}
        >
          <div className="modal-body">
            {isDoublesLeague && (
              <div className="apply-scope-section">
                <label className="form-label" id="apply-scope-label">
                  Apply to
                </label>
                <div
                  className="apply-scope-toggle"
                  role="group"
                  aria-labelledby="apply-scope-label"
                >
                  <button
                    type="button"
                    className={`apply-scope-option ${
                      applyTo === 'team' ? 'active' : ''
                    }`}
                    onClick={() => setApplyTo('team')}
                  >
                    Both of us
                  </button>
                  <button
                    type="button"
                    className={`apply-scope-option ${
                      applyTo === 'individual' ? 'active' : ''
                    }`}
                    onClick={() => setApplyTo('individual')}
                  >
                    Just me
                  </button>
                </div>
                {applyTo === 'individual' && (
                  <div className="apply-scope-warning" role="status">
                    <strong>Heads up</strong>
                    <span>
                      Setting availability just for yourself means your partner
                      still needs to enter theirs separately.
                    </span>
                    <button type="button" onClick={() => setApplyTo('team')}>
                      Switch back to "Both of us"
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Date — chip (create, prefilled) or input (create, no date / editing) */}
            {mode === 'create' ? (
              !date || isDateEditing ? (
                <div className="form-group">
                  <label htmlFor="availability-date" className="form-label">
                    Date
                  </label>
                  <div className="date-editing-row">
                    <input
                      id="availability-date"
                      type="date"
                      data-testid="date-input"
                      value={date}
                      onChange={(e) => setDate(e.target.value)}
                      className="form-input"
                      min={new Date().toISOString().split('T')[0]}
                      required
                    />
                    {date && (
                      <button
                        type="button"
                        className="date-done-btn"
                        onClick={() => setIsDateEditing(false)}
                      >
                        Done
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="date-chip-row" data-testid="date-chip-row">
                  <span className="date-chip" data-testid="date-chip">
                    📅{' '}
                    {format(
                      new Date(date.replace(/-/g, '/')),
                      'EEE, MMM d, yyyy',
                    )}
                  </span>
                  <button
                    type="button"
                    className="date-edit-btn"
                    onClick={() => setIsDateEditing(true)}
                    aria-label="Edit date"
                  >
                    ✎ Edit
                  </button>
                </div>
              )
            ) : (
              <div className="date-display">
                <strong>
                  {date
                    ? format(new Date(date), 'EEEE, MMM d, yyyy')
                    : 'Date not set'}
                </strong>
              </div>
            )}

            {/* Time inputs — always 2-column */}
            <div className="time-inputs">
              <div className="input-group">
                <label htmlFor="start-time">Start Time:</label>
                <select
                  id="start-time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  required
                >
                  <option value="">Select start time</option>
                  {allTimeOptions.slice(0, -1).map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="input-group">
                <label htmlFor="end-time">End Time:</label>
                <select
                  id="end-time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  required
                  disabled={!startTime}
                >
                  <option value="">
                    {startTime ? 'Select end time' : 'Select start time first'}
                  </option>
                  {endTimeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Recurrence — progressive disclosure, create mode only */}
            {mode === 'create' && (
              <div className="recurrence-section">
                {!isRecurring ? (
                  <button
                    type="button"
                    className="recurring-toggle-link"
                    onClick={() => setIsRecurring(true)}
                  >
                    + Make recurring
                  </button>
                ) : (
                  <>
                    <div className="recurrence-header-row">
                      <span className="recurrence-label">Repeating weekly</span>
                      <button
                        type="button"
                        className="recurring-collapse-btn"
                        onClick={() => setIsRecurring(false)}
                      >
                        One-time only
                      </button>
                    </div>
                    <div className="recurrence-options">
                      <div className="form-group">
                        <label className="form-label">On these days</label>
                        <DayPicker
                          selectedDays={selectedDays}
                          onChange={setSelectedDays}
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="interval" className="form-label">
                          Repeat every
                        </label>
                        <div className="interval-control">
                          <select
                            id="interval"
                            value={interval}
                            onChange={(e) =>
                              setInterval(parseInt(e.target.value))
                            }
                            className="form-select interval-select"
                          >
                            <option value="1">1</option>
                            <option value="2">2</option>
                          </select>
                          <span className="interval-label">week(s)</span>
                        </div>
                      </div>

                      <div className="form-group">
                        <label className="form-label">Ends</label>
                        <div className="end-options">
                          <label className="end-option">
                            <input
                              type="radio"
                              name="end-type"
                              checked={endType === 'league'}
                              onChange={() => setEndType('league')}
                            />
                            <span>
                              When league ends
                              {leagueEndDate && (
                                <span className="end-date-hint">
                                  {' '}
                                  (
                                  {new Date(leagueEndDate).toLocaleDateString(
                                    'en-US',
                                    {
                                      month: 'short',
                                      day: 'numeric',
                                      year: 'numeric',
                                    },
                                  )}
                                  )
                                </span>
                              )}
                            </span>
                          </label>
                          <label className="end-option">
                            <input
                              type="radio"
                              name="end-type"
                              checked={endType === 'date'}
                              onChange={() => setEndType('date')}
                            />
                            <span>On specific date</span>
                          </label>
                          {endType === 'date' && (
                            <input
                              type="date"
                              value={customEndDate}
                              onChange={(e) => setCustomEndDate(e.target.value)}
                              className="form-input custom-end-date"
                              min={new Date().toISOString().split('T')[0]}
                            />
                          )}
                        </div>
                      </div>

                      {getPreviewText() && (
                        <div className="recurrence-preview">
                          <strong>Preview:</strong> {getPreviewText()}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Edit-mode series actions — stays in scrollable body (advanced) */}
            {mode === 'edit' && existingAvailability?.is_recurring && (
              <div className="recurring-series-actions">
                <p className="series-actions-label">
                  Actions for entire series (keeps recurring pattern):
                </p>
                <div className="series-buttons">
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    disabled={loading}
                    onClick={handleUpdateSeries}
                  >
                    Update All Occurrences (Keeps Series)
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-danger"
                    onClick={handleDeleteSeries}
                    disabled={loading}
                  >
                    Delete Series
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* ── Sticky footer with primary actions ────────────────── */}
          <div className="modal-footer">
            {mode === 'edit' && existingAvailability?.is_recurring ? (
              <>
                <button
                  type="button"
                  className="btn btn-secondary btn-cancel"
                  onClick={onClose}
                  disabled={loading}
                >
                  Cancel
                </button>
                <div className="footer-primary-actions">
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={handleDeleteThisInstance}
                    disabled={loading}
                  >
                    Delete This Instance
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? 'Saving...' : 'Edit This Instance'}
                  </button>
                  <span
                    className="help-icon"
                    title="Editing this occurrence will create a standalone time slot with your changes. The recurring pattern will continue for all other dates."
                    onClick={() => {
                      alert(
                        'Editing this occurrence will create a standalone time slot with your changes. The recurring pattern will continue for all other dates.',
                      )
                    }}
                    style={{
                      marginLeft: '0.5rem',
                      cursor: 'help',
                      fontSize: '1.2em',
                      verticalAlign: 'middle',
                    }}
                  >
                    ❓
                  </span>
                </div>
              </>
            ) : mode === 'edit' ? (
              <>
                <button
                  type="button"
                  className="btn btn-secondary btn-cancel"
                  onClick={onClose}
                  disabled={loading}
                >
                  Cancel
                </button>
                <div className="footer-primary-actions">
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={handleDelete}
                    disabled={loading}
                  >
                    Delete
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? 'Saving...' : 'Update'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="btn btn-secondary btn-cancel"
                  onClick={onClose}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Saving...' : 'Add'}
                </button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  )
}

export default AvailabilityModal
